import React, { useState } from 'react';
import {
  Typography,
  Alert,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import InspectionsCharts from '../charts/InspectionsCharts';
import FullScreenTable from '../tables/FullScreenTable';
import UpgradeRequestDialog from '../UpgradeRequestDialog';
import logger from '../../utils/logger';

const InspectionsTab = ({
  searchData,
  getCurrentTableCounts,
  getCurrentTableData,
  getCurrentTableType,
  inspectionsTableSort,
  handleInspectionsTableSort,
  resetInspectionsTableSort,
  inspectionsFilter,
  setInspectionsFilter,
  resetInspectionsFilter,
  hiddenInspectionLines,
  setHiddenInspectionLines,
  resetHiddenInspectionLines,
  tierInfo,
}) => {
  // Local state for operator names popup
  const [operatorNamesDialogOpen, setOperatorNamesDialogOpen] = useState(false);
  const [fullScreenTableOpen, setFullScreenTableOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  // Local state for upgrade dialog
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  // Don't render if not inspections tab or no data
  if (getCurrentTableType() !== 'inspections' || !getCurrentTableData() || getCurrentTableData().length === 0) {
    return null;
  }

  const inspectionsData = getCurrentTableData();
  
  // Calculate unique operator names for this tab only
  const getTabOperatorNames = () => {
    if (!inspectionsData || inspectionsData.length === 0) return [];
    
    const operatorNames = new Set();
    inspectionsData.forEach(record => {
      const operator = record.operator_name || record['Operator Name'] || record.operator;
      if (operator && operator.toString().trim() !== '') {
        operatorNames.add(operator.toString().trim());
      }
    });
    
    return Array.from(operatorNames).sort();
  };
  
  // Calculate total inspections
  const totalInspections = inspectionsData.length;
  
  // Calculate compliance rate
  const complianceColumn = inspectionsData[0]['Compliance'] !== undefined ? 'Compliance' :
                        inspectionsData[0].compliance !== undefined ? 'compliance' : null;
  
  let complianceRate = 0;
  if (complianceColumn) {
    const compliantInspections = inspectionsData.filter(record => {
      const compliance = record[complianceColumn];
      return compliance && compliance.toString().toUpperCase() === 'YES';
    }).length;
    complianceRate = totalInspections > 0 ? (compliantInspections / totalInspections) * 100 : 0;
  }
  
  // Calculate date range
  const dateColumn = inspectionsData[0]['Inspection Date'] !== undefined ? 'Inspection Date' :
                   inspectionsData[0].inspection_date !== undefined ? 'inspection_date' : null;
  
  // Calculate date range
  let dateRangeText = '';
  if (dateColumn) {
    const dates = inspectionsData
      .map(record => record[dateColumn])
      .filter(date => date)
      .map(date => new Date(date))
      .filter(date => !isNaN(date.getTime()));
    
    if (dates.length > 0) {
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      };
      
      // Check if dates are in the same month/year
      if (minDate.getFullYear() === maxDate.getFullYear() && minDate.getMonth() === maxDate.getMonth()) {
        dateRangeText = ` (${formatDate(minDate)})`;
      } else {
        dateRangeText = `, with occurances from ${formatDate(minDate)} to ${formatDate(maxDate)}`;
      }
    }
  }

  return (
    <>
      {/* Inspections Findings Card */}
      <Card elevation={0} sx={{ mb: 3, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" component="h2">
              Inspections
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
              <Tooltip title="About Inspections" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setAboutDialogOpen(true)}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Raw Data" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setFullScreenTableOpen(true)}
                  disabled={tierInfo && !tierInfo.can_view_raw_data}
                  sx={{
                    opacity: (tierInfo && !tierInfo.can_view_raw_data) ? 0.5 : 1,
                    cursor: (tierInfo && !tierInfo.can_view_raw_data) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <TableChartIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Content */}
          <Box>
            {/* Unique Operator Names */}
            <Box sx={{ mb: 2, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>
                  Unique Operator Names — {getTabOperatorNames().length.toLocaleString()}
                </Typography>
                <Tooltip title="View all unique operator names" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setOperatorNamesDialogOpen(true)}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {/* Divider */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, mt: 2 }} />
            
            {/* Findings Content */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                The inspection data review discovered{' '}
                <strong>{totalInspections.toLocaleString()}</strong> inspection{totalInspections !== 1 ? 's' : ''} associated with the subject assets{dateRangeText}, and showing a{' '}
                <strong>{complianceRate.toFixed(1)}%</strong> compliance rate.
              </Typography>

              {/* Analytics Scope Notice */}
              {getCurrentTableData().length < getCurrentTableCounts() && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Analytics are based on {getCurrentTableData().length.toLocaleString()} of {getCurrentTableCounts().toLocaleString()} identified records 
                    (limited to first {getCurrentTableData().length.toLocaleString()} rows).
                  </Typography>
                </Alert>
              )}
            </Box>
          </Box>
          
          {tierInfo && !tierInfo.can_view_raw_data && (
            <Typography variant="body2" color="primary.main" sx={{ 
              mt: 2, 
              mb: 1, 
              fontWeight: 500 
            }}>
              💡 <button 
                onClick={() => setUpgradeDialogOpen(true)}
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Upgrade to Pro to view raw data tables
              </button>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Inspections Charts */}
      <InspectionsCharts
        inspectionsData={getCurrentTableData()}
        onSortInspectionsNotCompliant={handleInspectionsTableSort}
        inspectionsNotCompliantSortField={inspectionsTableSort.field}
        inspectionsNotCompliantSortDirection={inspectionsTableSort.direction}
        onResetInspectionsNotCompliant={resetInspectionsTableSort}
        inspectionsFilter={inspectionsFilter}
        setInspectionsFilter={setInspectionsFilter}
        resetInspectionsFilter={resetInspectionsFilter}
        hiddenInspectionLines={hiddenInspectionLines}
        setHiddenInspectionLines={setHiddenInspectionLines}
        resetHiddenInspectionLines={resetHiddenInspectionLines}
      />

      {/* About Dialog */}
      <Dialog
        open={aboutDialogOpen}
        onClose={() => setAboutDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          About Inspections
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography paragraph>
              State regulators conduct regular field inspections of oil and gas operations to ensure compliance with state standards and regulations.
            </Typography>
            <Typography paragraph>
              Each year, field inspectors conduct more than 350,000 inspections. With an aggressive inspection schedule, inspectors ensure every well in Texas is inspected at least once every five years, while also inspecting other oil and gas facilities such as tank batteries, surface disposal sites, and drilling rigs.
            </Typography>
            <Typography paragraph>
              Emergency responses and public complaints receive the highest priority. Other inspections are prioritized based on factors such as established performance goals, proximity to public or sensitive areas, operator compliance history, and area-specific knowledge or concerns.
            </Typography>
            <Typography paragraph sx={{ mt: 3 }}>
              <strong>Compliance status definitions</strong>
            </Typography>
            <Typography paragraph>
              <strong>Yes</strong> – All state rules inspected were found to be compliant during the inspection.
            </Typography>
            <Typography paragraph>
              <strong>No</strong> – At least one state rule was found to be non-compliant during the inspection and confirmed as non-compliant through regulatory technical review.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operator Names Dialog */}
      <Dialog
        open={operatorNamesDialogOpen}
        onClose={() => setOperatorNamesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Unique Operator Names
            </Typography>
            <IconButton
              onClick={() => setOperatorNamesDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing unique operator names from current search results ({getCurrentTableData().length.toLocaleString()} records)
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {getTabOperatorNames().length > 0 ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total: {getTabOperatorNames().length.toLocaleString()} unique operators</strong>
                </Typography>
                <List dense>
                  {getTabOperatorNames().map((operator, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText primary={operator} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography>No operator data available</Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Full Screen Table Dialog */}
      <FullScreenTable
        open={fullScreenTableOpen}
        onClose={() => setFullScreenTableOpen(false)}
        tableType="inspections"
        data={inspectionsData}
        allColumns={inspectionsData.length > 0 ? Object.keys(inspectionsData[0]) : []}
        defaultColumns={['operator_name', 'lease_facility_name', 'inspection_date', 'compliance']}
        totalCount={getCurrentTableCounts()}
        rowLimit={tierInfo?.table_limits?.inspections || 1000}
        onReset={() => {
          // Reset any inspections-specific state if needed
          logger.debug('Inspections table reset');
        }}
      />
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </>
  );
};

export default InspectionsTab;